import React, { useCallback, useId, useRef, useState } from 'react';
import logoGea from 'assets/images/logo-gea.png';
import type { FooterProps, FooterSocialLink } from './types';
import { sanitizeAboutHtml } from './sanitizeHtml';
import { FooterModal } from './FooterModal';
import { LEGAL_INFORMATION_TITLE, LegalInformationBody } from './LegalInformationBody';
import { DefaultSocialIcon, SOCIAL_ICONS } from '../header/icons';
import type { SocialNetwork } from '../header/types';
import { ROUTE_KEYS } from 'legacyfrontend/constants';
import './Footer.css';

const DEFAULT_ABOUT_US_COLOR = '#273854';
const ABOUT_EMPTY_MESSAGE = 'Todavía no se agregó información.';
const OTHER_LINKS_EMPTY_MESSAGE = 'Todavía no se agregaron enlaces.';

const ABOUT_US_TITLE = 'Sobre nosotros';
const OTHER_LINKS_TITLE = 'Otros enlaces';

const PAIS_ID_ARGENTINA = 1;
const PAIS_ID_PERU = 2;
const PAIS_ID_MEXICO = 4;

const SOCIAL_LABEL: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  twitter: 'Twitter',
  x: 'X',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
};

function safeHref(url?: string | null): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('/') || trimmed.startsWith('#')) return trimmed;
  return null;
}

function networkLabel(network: string): string {
  const key = network.toLowerCase();
  return SOCIAL_LABEL[key] ?? network;
}

const PhoneIcon: React.FC = () => (
  <svg aria-hidden='true' viewBox='0 0 24 24'>
    <path d='M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C9.6 21 3 14.4 3 6c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .7-.2 1l-2.3 2.2z' />
  </svg>
);

const MailIcon: React.FC = () => (
  <svg aria-hidden='true' viewBox='0 0 24 24'>
    <path d='M4 6h16c.6 0 1 .4 1 1v10c0 .6-.4 1-1 1H4c-.6 0-1-.4-1-1V7c0-.6.4-1 1-1zm0 2v.4l8 5 8-5V8H4zm0 2.8V17h16v-6.2l-7.4 4.6c-.4.2-.8.2-1.2 0L4 10.8z' />
  </svg>
);

const PinIcon: React.FC = () => (
  <svg aria-hidden='true' viewBox='0 0 24 24'>
    <path d='M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z' />
  </svg>
);

const ClockIcon: React.FC = () => (
  <svg aria-hidden='true' viewBox='0 0 24 24'>
    <path d='M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm1 10.41 4.29 4.3-1.42 1.41L11 13.41V6h2Z' />
  </svg>
);

const SocialIcon: React.FC<{ network: string }> = ({ network }) => {
  const Icon = SOCIAL_ICONS[network.toLowerCase() as SocialNetwork] ?? DefaultSocialIcon;
  return <Icon />;
};

const Footer: React.FC<FooterProps> = ({
  config,
  contact,
  officeInfo,
  onModalOpen,
  onModalClose,
}) => {
  const {
    backgroundColor = '#54a5e8',
    aboutUsHtml = '',
    aboutUsColor = DEFAULT_ABOUT_US_COLOR,
    showPaymentMethods = false,
    showLegalConditions = false,
    showOtherLinks = false,
    showLegalInformation = false,
    showCancellationLink = false,
    otherLinks = [],
    footerLogos = [],
    address = '',
    hours = '',
  } = config;

  const {
    phone = '',
    showPhone = false,
    email = '',
    showEmail = false,
    socialLinks = [],
  } = contact ?? {};

  const trimmedAddress = address.trim();
  const trimmedHours = hours.trim();

  const { razonSocial, legajo, cuit, paisId } = officeInfo ?? {};
  // Peru shows RUC instead of Legajo/CUIT; Mexico's real footer shows neither (see
  // legacyfrontend's COUNTRY_CONFIGS, which leaves México's registration/taxId labels empty).
  const officeInfoLines =
    paisId === PAIS_ID_PERU
      ? [razonSocial, cuit && `RUC ${cuit}`]
      : paisId === PAIS_ID_MEXICO
        ? [razonSocial]
        : [razonSocial, legajo && `Legajo ${legajo}`, cuit && `CUIT ${cuit}`];
  const hasOfficeInfo = officeInfoLines.some(Boolean);
  const showArgentinaLegalInformation = showLegalInformation && paisId === PAIS_ID_ARGENTINA;
  const visibleSocialLinks: FooterSocialLink[] = socialLinks.filter(
    (social) => social.show && safeHref(social.url),
  );

  const aboutTitleId = useId();
  const otherTitleId = useId();
  const legalTitleId = useId();

  const aboutTriggerRef = useRef<HTMLButtonElement>(null);
  const otherTriggerRef = useRef<HTMLButtonElement>(null);
  const legalTriggerRef = useRef<HTMLButtonElement>(null);
  const aboutCloseRef = useRef<HTMLButtonElement>(null);
  const otherCloseRef = useRef<HTMLButtonElement>(null);
  const legalCloseRef = useRef<HTMLButtonElement>(null);

  const [aboutOpen, setAboutOpen] = useState(false);
  const [otherOpen, setOtherOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);

  const openAbout = useCallback(() => {
    setAboutOpen(true);
    onModalOpen?.();
  }, [onModalOpen]);
  const closeAbout = useCallback(() => {
    setAboutOpen(false);
    aboutTriggerRef.current?.focus();
    onModalClose?.();
  }, [onModalClose]);
  const openOther = useCallback(() => {
    setOtherOpen(true);
    onModalOpen?.();
  }, [onModalOpen]);
  const closeOther = useCallback(() => {
    setOtherOpen(false);
    otherTriggerRef.current?.focus();
    onModalClose?.();
  }, [onModalClose]);
  const openLegal = useCallback(() => {
    setLegalOpen(true);
    onModalOpen?.();
  }, [onModalOpen]);
  const closeLegal = useCallback(() => {
    setLegalOpen(false);
    legalTriggerRef.current?.focus();
    onModalClose?.();
  }, [onModalClose]);

  const phoneHref = phone ? `tel:${phone.replace(/[^+\d]/g, '')}` : null;
  const emailHref = email ? `mailto:${email}` : null;

  const sanitizedAbout = sanitizeAboutHtml(aboutUsHtml);
  const hasAboutContent = sanitizedAbout.trim().length > 0;

  return (
    <>
      <footer className='site-footer' style={{ '--brand': backgroundColor } as React.CSSProperties}>
        <div className='footer-inner'>
          <section className='footer-column'>
            <h3>Contacto</h3>

            <h4>
              <button
                className='footer-about-link'
                onClick={openAbout}
                ref={aboutTriggerRef}
                type='button'
              >
                {ABOUT_US_TITLE}
              </button>
            </h4>

            <h4>
              <a
                className='footer-about-link'
                href={ROUTE_KEYS.AGENCIES}
                rel='noreferrer'
                target='_blank'
              >
                Oficinas
              </a>
            </h4>

            {(emailHref && showEmail) ||
            (phoneHref && showPhone) ||
            trimmedAddress ||
            trimmedHours ? (
              <div className='footer-contact-lines'>
                {showEmail && emailHref && (
                  <a className='footer-contact-line' href={emailHref}>
                    <MailIcon />
                    <span>{email}</span>
                  </a>
                )}
                {showPhone && phoneHref && (
                  <a className='footer-contact-line' href={phoneHref}>
                    <PhoneIcon />
                    <span>{phone}</span>
                  </a>
                )}
                {trimmedAddress ? (
                  <span className='footer-contact-line'>
                    <PinIcon />
                    <span>{trimmedAddress}</span>
                  </span>
                ) : null}
                {trimmedHours ? (
                  <span className='footer-contact-line'>
                    <ClockIcon />
                    <span>{trimmedHours}</span>
                  </span>
                ) : null}
              </div>
            ) : null}

            {hasOfficeInfo && (
              <p className='footer-office'>
                {officeInfoLines.filter(Boolean).map((line, index, arr) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < arr.length - 1 ? <br /> : null}
                  </React.Fragment>
                ))}
              </p>
            )}

            {visibleSocialLinks.length > 0 && (
              <div className='footer-socials'>
                {visibleSocialLinks.map((social, index) => {
                  const href = safeHref(social.url);
                  if (!href) return null;
                  return (
                    <a
                      aria-label={networkLabel(social.network)}
                      className='footer-social'
                      href={href}
                      key={`${social.network}-${index}`}
                      rel='noreferrer noopener'
                      target='_blank'
                    >
                      <SocialIcon network={social.network} />
                    </a>
                  );
                })}
              </div>
            )}
          </section>

          <section className='footer-column'>
            <h3>Enlaces</h3>
            <div className='footer-links'>
              {paisId === PAIS_ID_PERU ? (
                <>
                  {showLegalConditions && (
                    <a
                      className='footer-link'
                      href={ROUTE_KEYS.TERMS_AND_CONDITIONS}
                      rel='noreferrer'
                      target='_blank'
                    >
                      Términos y Condiciones de Uso
                    </a>
                  )}
                  {/* Libro de Reclamaciones is a legal requirement in Peru — never toggleable off. */}
                  <a
                    className='footer-link'
                    href={ROUTE_KEYS.CLAIMS}
                    rel='noreferrer'
                    target='_blank'
                  >
                    Libro de Reclamaciones
                  </a>
                  {showPaymentMethods && (
                    <a
                      className='footer-link'
                      href={ROUTE_KEYS.PAY_METHODS}
                      rel='noreferrer'
                      target='_blank'
                    >
                      Formas de pago
                    </a>
                  )}
                  {showLegalInformation && (
                    <a
                      className='footer-link'
                      href={ROUTE_KEYS.PRIVACY_POLICY}
                      rel='noreferrer'
                      target='_blank'
                    >
                      Política de Privacidad
                    </a>
                  )}
                </>
              ) : paisId === PAIS_ID_MEXICO ? (
                <>
                  {showLegalInformation && (
                    <a
                      className='footer-link'
                      href={ROUTE_KEYS.PRIVACY_POLICY}
                      rel='noreferrer'
                      target='_blank'
                    >
                      Políticas de Privacidad
                    </a>
                  )}
                  {showLegalConditions && (
                    <a
                      className='footer-link'
                      href={ROUTE_KEYS.GENERAL_CONDITIONS}
                      rel='noreferrer'
                      target='_blank'
                    >
                      Condiciones generales de Contratación
                    </a>
                  )}
                </>
              ) : (
                <>
                  {showPaymentMethods && (
                    <a
                      className='footer-link'
                      href={ROUTE_KEYS.PAY_METHODS}
                      rel='noreferrer'
                      target='_blank'
                    >
                      Formas de pagos
                    </a>
                  )}
                  {showLegalConditions && (
                    <a
                      className='footer-link'
                      href={ROUTE_KEYS.GENERAL_CONDITIONS}
                      rel='noreferrer'
                      target='_blank'
                    >
                      Condiciones generales de Contratación
                    </a>
                  )}
                  {showOtherLinks && (
                    <button
                      aria-controls={otherTitleId}
                      className='footer-link footer-link--button'
                      onClick={openOther}
                      ref={otherTriggerRef}
                      type='button'
                    >
                      Otros Enlaces
                    </button>
                  )}
                  {showArgentinaLegalInformation && (
                    <button
                      aria-controls={legalTitleId}
                      className='footer-link footer-link--button'
                      onClick={openLegal}
                      ref={legalTriggerRef}
                      type='button'
                    >
                      {LEGAL_INFORMATION_TITLE}
                    </button>
                  )}
                  {showCancellationLink && (
                    <a
                      className='footer-link'
                      href={ROUTE_KEYS.ARREPENTIMIENTO}
                      rel='noreferrer'
                      target='_blank'
                    >
                      Botón de arrepentimiento
                    </a>
                  )}
                </>
              )}
            </div>
          </section>

          <div className='footer-logos-row'>
            <span aria-hidden='true' className='footer-rule' />
            <img alt='GEA' className='footer-gea' src={logoGea} />
            {footerLogos.map((logo, index) => {
              const href = safeHref(logo.linkUrl);
              const img = (
                <img alt={logo.name} className='footer-partner-logo' src={logo.imageUrl} />
              );
              return href ? (
                <a
                  aria-label={logo.name}
                  className='footer-partner-logo-link'
                  href={href}
                  key={`${logo.name}-${index}`}
                  rel='noreferrer noopener'
                  target='_blank'
                >
                  {img}
                </a>
              ) : (
                <span key={`${logo.name}-${index}`}>{img}</span>
              );
            })}
          </div>
        </div>
      </footer>

      <FooterModal
        closeRef={aboutCloseRef}
        labelledBy={aboutTitleId}
        onClose={closeAbout}
        open={aboutOpen}
        title={ABOUT_US_TITLE}
      >
        {hasAboutContent ? (
          <div
            className='footer-modal-rich-text'
            dangerouslySetInnerHTML={{ __html: sanitizedAbout }}
            style={{ color: aboutUsColor }}
          />
        ) : (
          <p className='footer-modal-empty'>{ABOUT_EMPTY_MESSAGE}</p>
        )}
      </FooterModal>

      <FooterModal
        closeRef={otherCloseRef}
        labelledBy={otherTitleId}
        onClose={closeOther}
        open={otherOpen}
        title={OTHER_LINKS_TITLE}
      >
        {otherLinks.length === 0 ? (
          <p className='footer-modal-empty'>{OTHER_LINKS_EMPTY_MESSAGE}</p>
        ) : (
          <div className='footer-modal-links'>
            {otherLinks.map((link, index) => {
              const href = safeHref(link.url);
              if (!href) return null;
              return (
                <a
                  href={href}
                  key={`${link.text}-${index}`}
                  rel='noreferrer noopener'
                  target='_blank'
                >
                  <span>{link.text}</span>
                  <span aria-hidden='true'>↗</span>
                </a>
              );
            })}
          </div>
        )}
      </FooterModal>

      {legalOpen ? (
        <FooterModal
          closeRef={legalCloseRef}
          labelledBy={legalTitleId}
          onClose={closeLegal}
          open={legalOpen}
          title={LEGAL_INFORMATION_TITLE}
        >
          <LegalInformationBody paisId={paisId} />
        </FooterModal>
      ) : null}
    </>
  );
};

export default Footer;
